import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../api/apiClient';
import MainLayout from '../components/layout/MainLayout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';
// RoleEditor removed as per requirements
import UserFormModal from '../components/admin/UserFormModal';
import TenantDropdown from '../components/admin/TenantDropdown';
// Flow assignments are now managed directly in the Flow editor
import { toast } from 'react-toastify';
import { subscribe, EVENTS } from '../utils/events';
import { getFirestore, collection, query, onSnapshot } from 'firebase/firestore';
import { getSites } from '../api/backendApi';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import { canManageAssignedTenant } from './Admin/dealerAdminNav';
import { ROLES, ROUTES } from '../utils/constants';
import { userLabel } from '../utils/userLabel';
import {
  readSupportedTenantSession,
  subscribeSupportedTenantSession,
} from './Admin/supportedTenantsSession';

const UserManagment = ({
  pageTitle = 'User Management',
  showBackButton = false,
  embedded = false,
  tenantId: tenantIdProp,
  tenantName: tenantNameProp,
}) => {
  const { currentUser, isAdmin, userClaims, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const [supportSession, setSupportSession] = useState(() => readSupportedTenantSession());
  useEffect(() => subscribeSupportedTenantSession(() => {
    setSupportSession(readSupportedTenantSession());
  }), []);
  const userRole = userClaims?.role || userData?.role;
  const isTenantAdmin = userRole === 'tenant_admin';
  const dealerTenantActive = Boolean(dealerAdmin.active && dealerAdmin.tenantId);
  const supportTenantId = !dealerTenantActive ? (supportSession?.tenantId || '') : '';
  const canAccess = canManageAssignedTenant({
    dealerActive: dealerTenantActive,
    isAdmin,
    isTenantAdmin,
  });
  const scopedTenantId = tenantIdProp || (dealerTenantActive ? dealerAdmin.tenantId : '') || supportTenantId || userClaims?.tenantId || userData?.tenantId;
  const editorRole = dealerTenantActive ? ROLES.TENANT_ADMIN : userRole;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed selectedUser state as Quick Edit functionality is no longer needed
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [siteMap, setSiteMap] = useState({});
  const unsubscribeRef = useRef(null);
  const unsubscribeSitesRef = useRef(null);
  // Flow assignments are now managed directly in the Flow editor, so we no longer track a separate
  // "Manage Assignments" modal here.

  // --- Header with Title and Back Button ---
  // Render above all cards and content
  const renderHeader = () => (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 mt-2 gap-2">
      <h1 className="text-2xl font-bold text-gray-800 mb-2 sm:mb-0">{pageTitle}</h1>
      {isTenantAdmin && !dealerTenantActive && !supportTenantId ? (
        <Link to={ROUTES.ADMIN_ASK_SB} data-testid="ask-sb-integrations-link" className="text-sm text-blue-700 hover:underline">
          Integrations
        </Link>
      ) : null}
      {/* {showBackButton && (
        <Button
          to="/admin"
          variant="secondary"
          className="ml-0 sm:ml-4"
        >
          Back to Admin Dashboard
        </Button>
      )} */}
    </div>
  );

  // Function to fetch users from backend
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const tenantId = scopedTenantId;
      const basePath = '/users/list';
      const endpoint = tenantId && (isTenantAdmin || tenantIdProp || dealerTenantActive || supportTenantId)
        ? `${basePath}?tenantId=${encodeURIComponent(tenantId)}`
        : basePath;

      console.log('🔄 Fetching users from', endpoint, '...');
      const response = await authenticatedFetch(endpoint);
      console.log('🔍 Raw response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      // Clone the response before parsing to avoid "body already read" errors
      const responseClone = response.clone();

      try {
        // Parse JSON and handle any parsing errors
        const text = await responseClone.text();
        console.log(
          '📝 Raw response text:',
          text.substring(0, 200) + (text.length > 200 ? '...' : '')
        );

        const data = await response.json();
        console.log('📊 Parsed user data type:', typeof data);
        console.log('📊 Is array?', Array.isArray(data));
        console.log('📊 Has users property?', data && typeof data === 'object' && 'users' in data);
        console.log('📊 Full data:', data);

        // Determine the correct user array based on response shape
        let userArray = [];

        if (Array.isArray(data)) {
          console.log('📋 Response is an array of users');
          userArray = data;
        } else if (data && data.users && Array.isArray(data.users)) {
          console.log('📋 Response has users array property');
          userArray = data.users;
        } else if (data && data.data && Array.isArray(data.data)) {
          console.log('📋 Response has data array property');
          userArray = data.data;
        } else if (data && typeof data === 'object') {
          console.log('📋 Response is an object, extracting user-like objects');
          // Extract any objects that look like users (have email property)
          userArray = Object.values(data)
            .filter(item => item && typeof item === 'object' && (Array.isArray(item) || item.email))
            .flat();
        }

        console.log(`✅ Found ${userArray.length} users:`, userArray);

        // Update state with users
        setUsers(userArray);

        // Show appropriate feedback based on result
        if (userArray.length === 0) {
          console.warn('⚠️ No users found in the response');
        }

        setError(null);
      } catch (parseError) {
        console.error('❌ Error parsing response:', parseError);
        throw new Error(`Failed to parse user data: ${parseError.message}`);
      }
    } catch (err) {
      console.error('❌ Error fetching users:', err);
      setError('Failed to load users. Check your permissions.');
      toast.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Members use the Firestore subscription. Support context has no membership,
  // so those reads go through the authorized sites API.
  useEffect(() => {
    const tenantId = scopedTenantId;
    if (!currentUser?.uid || !tenantId) return undefined;
    if (supportSession?.tenantId) {
      let cancelled = false;
      getSites(tenantId).then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const sitesMap = {};
        rows.forEach((site) => {
          const id = site?.id || site?.siteId;
          if (id) sitesMap[id] = site?.name || id;
        });
        setSiteMap(sitesMap);
      }).catch((error) => {
        console.error('❌ Error loading sites:', error);
      });
      return () => {
        cancelled = true;
      };
    }

    const db = getFirestore();
    const sitesRef = collection(db, 'tenants', tenantId, 'sites');
    const sitesQuery = query(sitesRef);

    console.log('🔄 Setting up real-time sites subscription...');
    unsubscribeSitesRef.current = onSnapshot(
      sitesQuery,
      (snapshot) => {
        const sitesMap = {};
        snapshot.docs.forEach((doc) => {
          sitesMap[doc.id] = doc.data().name || doc.id;
        });
        console.log(`✅ Sites loaded: ${Object.keys(sitesMap).length} sites`);
        setSiteMap(sitesMap);
      },
      (error) => {
        console.error('❌ Error loading sites:', error);
      }
    );

    return () => {
      if (unsubscribeSitesRef.current) {
        unsubscribeSitesRef.current();
      }
    };
  }, [currentUser?.uid, scopedTenantId, supportSession?.tenantId]);

  // Fetch users on component mount and subscribe to events
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    // Initial fetch
    fetchUsers();

    // Subscribe to user tenant update events
    unsubscribeRef.current = subscribe(EVENTS.USER_LIST_REFRESH, () => {
      console.log('🔄 User list refresh event received, refreshing users...');
      fetchUsers();
    });

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
    // eslint-disable-next-line
  }, [currentUser?.uid, scopedTenantId]);

  if (!currentUser || !canAccess) {
    const denied = (
        <div className="p-6 max-w-4xl mx-auto bg-red-50 border border-red-200 rounded-md text-center">
          <h1 className="text-xl font-bold text-red-700 mb-2">Access Denied</h1>
          <p className="text-red-600">This page is only accessible to administrators.</p>
        </div>
    );
    return embedded ? denied : <MainLayout>{denied}</MainLayout>;
  }

  const cellPad = embedded ? 'px-3 py-3' : 'px-6 py-4';
  const showTenantColumn = !embedded;
  const emptyColSpan = 4 + (showTenantColumn ? 1 : 0) + 1;

  const openEditUser = (user) => {
    setModalUser(user);
    setIsNewUser(false);
    setIsModalOpen(true);
  };

  const addUserButton = (
    <Button
      onClick={() => {
        setModalUser(null);
        setIsNewUser(true);
        setIsModalOpen(true);
      }}
    >
      Add New User
    </Button>
  );

  const content = (
      <Box sx={{ maxWidth: '100%', mx: 'auto', px: embedded ? 0 : { xs: 2, md: 3 } }}>
        {!embedded && renderHeader()}

      {/* User Form Modal */}
      {isModalOpen && (
        <UserFormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setModalUser(null);
          }}
          isNew={isNewUser}
          user={modalUser}
          onSave={fetchUsers}
          editorRole={editorRole}
          defaultTenantId={scopedTenantId}
          defaultTenantName={tenantNameProp}
        />
      )}
      <Card
        title={embedded ? 'Users' : 'User Management'}
        titleRight={addUserButton}
        padding={embedded ? 'p-3' : 'p-6'}
      >
        {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
        {loading ? (
          <LoadingSpinner text="Loading users..." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className={`${cellPad} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                  >
                    User
                  </th>
                  <th
                    scope="col"
                    className={`${cellPad} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className={`${cellPad} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className={`${cellPad} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                  >
                    Site
                  </th>
                  {showTenantColumn && (
                  <th
                    scope="col"
                    className={`${cellPad} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                  >
                    Tenant
                  </th>
                  )}
                  <th
                    scope="col"
                    className={`${cellPad} text-right text-xs font-medium text-gray-500 uppercase tracking-wider`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={emptyColSpan} className={`${cellPad} text-center text-sm text-gray-500`}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map(user => {
                    const userId = user.id || user.uid;
                    return (
                    <tr
                      key={userId}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => openEditUser(user)}
                    >
                      <td className={`${cellPad} whitespace-nowrap`}>
                        <div className="flex items-center">
                          {user.photoURL ? (
                            <img className="h-8 w-8 rounded-full mr-2" src={user.photoURL} alt="" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                              <span className="text-gray-500 text-sm">
                                {userLabel(user).charAt(0) || '?'}
                              </span>
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {userLabel(user)}
                          </div>
                        </div>
                      </td>
                      <td className={`${cellPad} whitespace-nowrap text-sm text-gray-500`}>{user.email}</td>
                      <td className={`${cellPad} whitespace-nowrap text-sm`}>
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.role === 'superadmin'
                              ? 'bg-red-100 text-red-800'
                              : user.role === 'admin'
                                ? 'bg-green-100 text-green-800'
                                : user.role === 'tenant_admin'
                                  ? 'bg-purple-100 text-purple-800'
                                  : user.role === 'site_admin'
                                    ? 'bg-indigo-100 text-indigo-800'
                                    : user.role === 'operator'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : user.role === 'viewer'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.role || 'unknown'}
                        </span>
                        {user.permissions && (
                          <div className="text-xs text-gray-500 mt-1">
                            {user.permissions.canConfigure && <span className="mr-1">⚙️</span>}
                            {user.permissions.canTriggerEvents && <span className="mr-1">🔔</span>}
                            {user.permissions.receivesNotifications && <span>📱</span>}
                          </div>
                        )}
                      </td>
                      <td className={`${cellPad} whitespace-nowrap text-sm text-gray-500`}>
                        {user.siteId ? (siteMap[user.siteId] || user.siteId) : '—'}
                      </td>
                      {showTenantColumn && (
                      <td className={`${cellPad} whitespace-nowrap text-sm text-gray-500`}>
                        {user.accessClass === 'inconsistent_legacy' ? (
                          <div>
                            <div title={user.recovery || ''}>
                              No active membership{user.tenantName ? ` (legacy record: ${user.tenantName})` : ''}
                            </div>
                            {isAdmin && !dealerTenantActive ? (
                              <TenantDropdown
                                userId={user.uid}
                                currentTenantId={user.tenantId}
                                onTenantAssigned={() => {}}
                              />
                            ) : null}
                          </div>
                        ) : dealerTenantActive ? (
                          dealerAdmin.tenantName || '—'
                        ) : isAdmin ? (
                          <TenantDropdown
                            userId={user.uid}
                            currentTenantId={user.tenantId}
                            onTenantAssigned={() => {}}
                          />
                        ) : user.accessClass === 'no_access' ? (
                          'No access'
                        ) : user.accessClass === 'support_grant' ? (
                          'Support grant'
                        ) : (
                          user.tenantName || '—'
                        )}
                      </td>
                      )}
                      <td className={`${cellPad} whitespace-nowrap text-right text-sm font-medium`}>
                        <div className="flex space-x-2 justify-end">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="px-3 py-1"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditUser(user);
                            }}
                          >
                            Edit User
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="px-3 py-1"
                            disabled={user.email === currentUser.email}
                            onClick={async (event) => {
                              event.stopPropagation();
                              if (user.email === currentUser.email) {
                                toast.error('You cannot delete your own account.');
                                return;
                              }
                              if (
                                !window.confirm(
                                  `Are you sure you want to delete user ${user.email}? This action cannot be undone.`
                                )
                              )
                                return;
                              try {
                                const response = await authenticatedFetch(
                                  `/users/delete/${user.id || user.uid}`,
                                  {
                                    method: 'DELETE',
                                  }
                                );
                                if (!response.ok) {
                                  const errorData = await response.json();
                                  toast.error(errorData.message || 'Failed to delete user');
                                  throw new Error(errorData.message || 'Failed to delete user');
                                }
                                toast.success('User deleted successfully!');
                                await fetchUsers();
                              } catch (err) {
                                toast.error(err.message || 'Failed to delete user');
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      </Box>
  );

  return embedded ? content : <MainLayout>{content}</MainLayout>;
};

export default UserManagment;
